import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { NotificationItem, NotificationVisualState } from '../models/notification-center.model';
import { CANAL_SOCKET, TIPO_RECEPTOR_USUARIO_SINERGIAL } from '../constants/notification-constants';
import { AuthService } from './auth.service';
import { ConfigService } from './config.service';
import { NotificationService } from './notification.service';
import { NotificacionHistorialService } from './notificacion-historial.service';
import { ConfigClientService } from './configClient.service';
import { SINERGIAL_ROUTES } from '../constants/api-sinergial-routes';

@Injectable({ providedIn: 'root' })
export class NotificationCenterService {
  private static readonly NOTIFICATION_HISTORY_PATH = SINERGIAL_ROUTES.NOTIFICACION_HISTORIAL.PATH_BASE;
  private static readonly NOTIFICATIONS_PAGE_SIZE = 5;
  private static readonly UNREAD_COUNT_PAGE_SIZE = 1;

  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly configClientService = inject(ConfigClientService);
  private readonly notificationService = inject(NotificationService);
  private readonly notificacionHistorialService = inject(NotificacionHistorialService);

  private readonly itemsSignal = signal<readonly NotificationItem[]>([]);
  private readonly unreadCountSignal = signal(0);
  private readonly currentPageSignal = signal(0);
  private readonly hasMoreSignal = signal(true);
  private readonly loadingMoreSignal = signal(false);
  private readonly loadingSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);
  private readonly initializedSignal = signal(false);

  readonly items = this.itemsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly loadingMore = this.loadingMoreSignal.asReadonly();
  readonly hasMore = this.hasMoreSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly unreadCount = this.unreadCountSignal.asReadonly();
  readonly visualState = computed<NotificationVisualState>(() => {
    if (this.loadingSignal()) {
      return 'loading';
    }

    if (this.errorSignal()) {
      return 'error';
    }

    return this.itemsSignal().length > 0 ? 'ready' : 'empty';
  });

  initialize(): void {
    if (this.initializedSignal()) {
      void this.reload();
      return;
    }

    this.initializedSignal.set(true);
    this.notificacionHistorialService.refreshListado.subscribe((shouldRefresh) => {
      if (!shouldRefresh) {
        return;
      }
      void this.reload();
    });
    void this.reload();
  }

  clear(): void {
    this.itemsSignal.set([]);
    this.unreadCountSignal.set(0);
    this.currentPageSignal.set(0);
    this.hasMoreSignal.set(true);
    this.loadingMoreSignal.set(false);
    this.errorSignal.set(null);
    this.loadingSignal.set(false);
  }

  async reload(): Promise<void> {
    this.loadingSignal.set(true);
    this.loadingMoreSignal.set(false);
    this.errorSignal.set(null);

    const userId = this.resolveCurrentUserId();
    if (!userId) {
      this.itemsSignal.set([]);
      this.unreadCountSignal.set(0);
      this.currentPageSignal.set(0);
      this.hasMoreSignal.set(false);
      this.loadingSignal.set(false);
      return;
    }

    const historialPromise = this.loadHistorial(userId, 1)
      .then((response) => {
        const normalized = this.deduplicate(this.normalizeResponse(response));
        this.itemsSignal.set(normalized);
        this.currentPageSignal.set(1);
        this.hasMoreSignal.set(
          this.resolveHasMore(
            response,
            normalized.length,
            NotificationCenterService.NOTIFICATIONS_PAGE_SIZE,
          ),
        );
      })
      .catch(() => {
        this.errorSignal.set('No se pudieron cargar las notificaciones.');
      });

    const unreadPromise = this.loadUnreadCount(userId)
      .then((response) => {
        this.unreadCountSignal.set(this.readTotalRecords(response));
      })
      .catch(() => {
        // Keep the last known badge value if the counter request fails.
      });

    await Promise.allSettled([historialPromise, unreadPromise]);
    this.loadingSignal.set(false);
  }

  async loadMore(): Promise<void> {
    if (this.loadingSignal() || this.loadingMoreSignal() || !this.hasMoreSignal()) {
      return;
    }

    const userId = this.resolveCurrentUserId();
    if (!userId) {
      this.hasMoreSignal.set(false);
      return;
    }

    this.loadingMoreSignal.set(true);
    this.errorSignal.set(null);

    const nextPage = this.currentPageSignal() + 1;

    try {
      const response = await this.loadHistorial(userId, nextPage);
      const normalized = this.deduplicate([
        ...this.itemsSignal(),
        ...this.normalizeResponse(response),
      ]);
      this.itemsSignal.set(normalized);
      this.currentPageSignal.set(nextPage);
      this.hasMoreSignal.set(
        this.resolveHasMore(
          response,
          normalized.length,
          NotificationCenterService.NOTIFICATIONS_PAGE_SIZE,
        ),
      );
    } catch {
      this.errorSignal.set('No se pudieron cargar más notificaciones.');
    } finally {
      this.loadingMoreSignal.set(false);
    }
  }

  markAsRead(notificationId: string): void {
    let wasUnread = false;
    this.itemsSignal.update((current) =>
      current.map((item) => {
        if (item.id !== notificationId || item.read) {
          return item;
        }

        wasUnread = true;
        return { ...item, read: true };
      }),
    );

    if (wasUnread) {
      this.unreadCountSignal.update((count) => Math.max(0, count - 1));
    }
  }

  simulateIncomingNotification(): void {
    const now = new Date().toISOString();
    const simulatedNotification: NotificationItem = {
      id: `sim-${Date.now()}`,
      title: 'Notificación de prueba',
      message: 'Esta notificación fue generada desde el dashboard para pruebas manuales.',
      createdAt: now,
      read: false,
      target: {
        route: '/info/novedades',
      },
    };

    this.itemsSignal.update((current) => [simulatedNotification, ...current]);
    this.unreadCountSignal.update((count) => count + 1);
    this.notificationService.info('Se generó una notificación de prueba.');
  }

  private loadHistorial(userId: string | null, pageNumber: number): Promise<unknown> {
    const endpoint = this.resolveEndpoint();
    const params = this.buildHistorialParams(
      userId,
      'N,L',
      NotificationCenterService.NOTIFICATIONS_PAGE_SIZE,
      pageNumber,
    );
    return firstValueFrom(this.http.get<unknown>(endpoint, { params }));
  }

  private loadUnreadCount(userId: string | null): Promise<unknown> {
    const endpoint = this.resolveEndpoint();
    const params = this.buildHistorialParams(
      userId,
      'N',
      NotificationCenterService.UNREAD_COUNT_PAGE_SIZE,
      1,
    );
    return firstValueFrom(this.http.get<unknown>(endpoint, { params }));
  }

  private resolveEndpoint(): string {
    return this.joinUrl(
      this.configClientService.getConfig()?.api_sinergial || '',
      NotificationCenterService.NOTIFICATION_HISTORY_PATH,
    );
  }

  private buildHistorialParams(
    userId: string | null,
    estados: string,
    pageSize: number,
    pageNumber: number,
  ): HttpParams {
    const params: Record<string, string> = {
      Orden: '7',
      SentidoOrden: 'desc',
      Estados: estados,
      IdsNotifCanales: String(CANAL_SOCKET),
      PageNumber: String(pageNumber),
      PageSize: String(pageSize),
    };

    if (userId) {
      params['IdsReceptoresConTipos'] = `[${userId}|${TIPO_RECEPTOR_USUARIO_SINERGIAL}]`;
    }

    return new HttpParams({
      fromObject: params,
    });
  }

  private resolveCurrentUserId(): string | null {
    const userId = this.authService.getCurrentUser()?.id;
    if (typeof userId === 'number' && Number.isFinite(userId)) {
      return String(userId);
    }
    if (typeof userId === 'string' && userId.trim().length > 0) {
      return userId.trim();
    }
    return null;
  }

  private normalizeResponse(payload: unknown): NotificationItem[] {
    const records = this.extractRecords(payload);
    return records.map((record) => this.toNotification(record));
  }

  private readTotalRecords(payload: unknown): number {
    if (!this.isObjectRecord(payload)) {
      return 0;
    }

    const totalRecords = payload['TotalRecords'];
    return typeof totalRecords === 'number' && Number.isFinite(totalRecords) ? totalRecords : 0;
  }

  private resolveHasMore(payload: unknown, currentCount: number, pageSize: number): boolean {
    const totalRecords = this.readTotalRecords(payload);
    if (totalRecords > 0) {
      return currentCount < totalRecords;
    }

    const records = this.extractRecords(payload);
    return records.length === pageSize;
  }

  private extractRecords(payload: unknown): Record<string, unknown>[] {
    if (Array.isArray(payload)) {
      return payload.filter(this.isObjectRecord);
    }

    if (!this.isObjectRecord(payload)) {
      return [];
    }

    const data = payload['Data'];
    if (Array.isArray(data)) {
      return data.filter(this.isObjectRecord);
    }

    if (this.isObjectRecord(data)) {
      const nested =
        data['historial'] ?? data['listado'] ?? data['items'] ?? data['notificaciones'];
      if (Array.isArray(nested)) {
        return nested.filter(this.isObjectRecord);
      }
    }

    const direct =
      payload['historial'] ?? payload['listado'] ?? payload['items'] ?? payload['notificaciones'];
    if (Array.isArray(direct)) {
      return direct.filter(this.isObjectRecord);
    }

    return [];
  }

  private toNotification(record: Record<string, unknown>): NotificationItem {
    const title =
      this.readString(record, ['title', 'titulo', 'Titulo', 'asunto', 'Asunto']) ?? 'Notificacion';
    const message =
      this.readString(record, ['message', 'Mensaje', 'descripcion', 'Descripcion']) ??
      'Nueva notificacion recibida.';
    const createdAt =
      this.readString(record, ['createdAt', 'fecha', 'Fecha', 'FechaAlta', 'FechaCreacion']) ??
      new Date().toISOString();
    const id =
      this.readString(record, ['id', 'Id', 'ID', 'IdNotificacion']) ??
      `${title}|${message}|${createdAt}`;
    const route =
      this.readString(record, ['route', 'ruta', 'Ruta', 'path']) ??
      this.readString(record, ['url', 'Url', 'URL']);
    const read =
      this.readBoolean(record, ['read', 'Read', 'leida', 'Leida', 'leido', 'Leido']) ??
      this.readString(record, ['FechaLectura']) !== null;

    return {
      id,
      title,
      message,
      createdAt,
      read,
      target: route
        ? /^https?:\/\//i.test(route)
          ? { externalUrl: route }
          : { route }
        : undefined,
    };
  }

  private deduplicate(items: readonly NotificationItem[]): readonly NotificationItem[] {
    const uniqueById = new Map<string, NotificationItem>();
    for (const item of items) {
      uniqueById.set(item.id, item);
    }

    return [...uniqueById.values()].sort(
      (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
    );
  }

  private readString(source: Record<string, unknown>, keys: readonly string[]): string | null {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
      if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
      }
    }
    return null;
  }

  private readBoolean(source: Record<string, unknown>, keys: readonly string[]): boolean | null {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'boolean') {
        return value;
      }
      if (typeof value === 'number') {
        return value > 0;
      }
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', '1', 'si', 'yes'].includes(normalized)) {
          return true;
        }
        if (['false', '0', 'no'].includes(normalized)) {
          return false;
        }
      }
    }
    return null;
  }

  private isObjectRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private joinUrl(baseUrl: string, path: string): string {
    const trimmedBase = baseUrl.replace(/\/+$/, '');
    const trimmedPath = path.replace(/^\/+/, '');
    return trimmedPath.length > 0 ? `${trimmedBase}/${trimmedPath}` : trimmedBase;
  }
}
